import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import clinicService from "../../../../../services/clinic.service";
import clinicbookingService from "../../../../../services/clinicbooking.service";

function AddEditDoctorInfo({ doctor, updateDoc, toogleEdit }) {
    const [clinicDoctors, setClinicDoctors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { clinicId } = useParams();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: doctor.id ? doctor : ""
    });

    useEffect(() => {
        getClinicDoctors();
        // Set form values when doctor prop changes
        if (doctor.id) {
            setValue("userId", doctor.userId);
            setValue("name", doctor.name);
            setValue("photoUrl", doctor.photoUrl || "");
            setValue("isActive", doctor.isActive ? "true" : "false");
            setValue("bio", doctor.bio || "");
        }
    }, [doctor, setValue]);

    const getClinicDoctors = () => {
        clinicService
            .getUsers(clinicId, "ECP")
            .then((res) => {
                setClinicDoctors(res.data);
            })
            .catch((err) => {
                console.log(err);
                toast.error("Failed to load doctors", {
                    position: toast.POSITION.BOTTOM_RIGHT
                });
            });
    };

    const onSubmit = (data) => {
        setIsSubmitting(true);

        let updateData = {
            ...data,
            isActive: data.isActive === "true",
            id: doctor.id || undefined
        };

        delete updateData.createdAt;
        delete updateData.modifiedAt;
        delete updateData.apptTypes;

        clinicbookingService.addUpdateDoctor(clinicId, updateData)
            .then(res => {
                toast.success(doctor.id ? "Doctor updated successfully!" : "Doctor added successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT
                });

                // Pass the updated doctor data to the parent component
                if (updateDoc) {
                    // If we have response data, use it, otherwise construct the updated object
                    const updatedDoctor = res.data || {
                        ...doctor,
                        ...updateData,
                        modifiedAt: new Date().toLocaleString() // Update the modified date
                    };
                    updateDoc(updatedDoctor);
                }

                // Close the edit form
                if (toogleEdit) toogleEdit();
            })
            .catch(err => {
                console.log(err);
                toast.error(err.response?.data?.message || "Failed to save doctor information", {
                    position: toast.POSITION.BOTTOM_RIGHT
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="card card-padding card-border shadow-none">
            <div className="d-flex align-items-center">
                <i className="bi bi-person-badge-fill text-primary me-2"></i>
                <div className="card-title mb-0">
                    {doctor.id ? "Edit Doctor Information" : "Add New Doctor"}
                </div>
            </div>

            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-4">
                        {/* Display Name */}
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="name" className="form-label small fw-semibold">
                                    Display Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    {...register("name", {
                                        required: "Display name is required",
                                        minLength: {
                                            value: 2,
                                            message: "Display name must be at least 2 characters"
                                        }
                                    })}
                                    placeholder="Enter display name as shown to patients"
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">{errors.name.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Photo URL */}
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="photoUrl" className="form-label small fw-semibold">
                                    Photo URL
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-link-45deg"></i>
                                    </span>
                                    <input
                                        type="url"
                                        className="form-control"
                                        {...register("photoUrl")}
                                        placeholder="https://example.com/photo.jpg"
                                    />
                                </div>
                                <small className="text-muted">
                                    Leave blank to use default avatar. URL should point to a square image.
                                </small>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="isActive" className="form-label small fw-semibold">
                                    Status <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.isActive ? 'is-invalid' : ''}`}
                                    {...register("isActive", { required: "Status is required" })}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                                {errors.isActive && (
                                    <div className="invalid-feedback">{errors.isActive.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Bio */}
                        <div className="col-12">
                            <div className="form-group">
                                <label htmlFor="bio" className="form-label small fw-semibold">
                                    Biography
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={5}
                                    {...register("bio")}
                                    placeholder="Enter doctor's biography, qualifications, specialties, and experience..."
                                ></textarea>
                                <small className="text-muted">
                                    This information will be visible to patients when booking appointments.
                                </small>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-12">
                            <div className="d-flex justify-content-end gap-2 pt-3 border-top">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={toogleEdit}
                                    disabled={isSubmitting}
                                >
                                    <i className="bi bi-x-circle me-2"></i>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-success btn-sm"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-floppy me-2"></i>
                                            {doctor.id ? "Save" : "Add Doctor"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Doctor Info Preview (if editing) */}
            {doctor.id && (
                <div className="card-footer bg-light">
                    <div className="row">
                        <div className="col-12">
                            <h6 className="text-muted mb-3">
                                <i className="bi bi-info-circle me-2"></i>
                                System Information
                            </h6>
                            <div className="d-flex flex-wrap gap-4">
                                <div>
                                    <small className="text-muted">Created:</small>
                                    <p className="mb-0 small">{doctor.createdAt}</p>
                                </div>
                                <div>
                                    <small className="text-muted">Last Modified:</small>
                                    <p className="mb-0 small">{doctor.modifiedAt}</p>
                                </div>
                                <div>
                                    <small className="text-muted">Doctor ID:</small>
                                    <p className="mb-0 small font-monospace">{doctor.userId}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AddEditDoctorInfo;